import { useState, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { QRCodeSVG } from "qrcode.react";
import { getImageUrl } from "../../utils/getImageUrl";
import {
  Mail, MessageSquare, X, Phone, MapPin, ShoppingBag,
  Star, ChevronDown, RefreshCw, UserCheck, UserX, Shield, Coins, Send,
} from "lucide-react";

dayjs.extend(relativeTime);
dayjs.locale("es");

export const ROLE_COLORS = {
  user:     "bg-blue-100 text-blue-700",
  company:  "bg-purple-100 text-purple-700",
  admin:    "bg-red-100 text-red-700",
  ryder:    "bg-orange-100 text-orange-700",
  delivery: "bg-teal-100 text-teal-700",
};

const ORDER_STATUS_COLORS = {
  pendiente:  "bg-yellow-100 text-yellow-700",
  confirmado: "bg-blue-100 text-blue-700",
  asignado:   "bg-indigo-100 text-indigo-700",
  en_camino:  "bg-orange-100 text-orange-700",
  entregado:  "bg-green-100 text-green-700",
  cancelado:  "bg-red-100 text-red-600",
};

const ORDER_STATUS_LABELS = {
  pendiente:  "Pendiente",
  confirmado: "Confirmado",
  asignado:   "Asignado",
  en_camino:  "En camino",
  entregado:  "Entregado",
  cancelado:  "Cancelado",
};

// ── Compose Message Modal ─────────────────────────────────────────────────────
function ComposeModal({ type, profile, onClose }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const isWA = type === "whatsapp";

  const handleSend = async () => {
    if (!message.trim()) return toast.error("El mensaje no puede estar vacío");
    if (!isWA && !subject.trim()) return toast.error("El asunto no puede estar vacío");
    setSending(true);
    try {
      const endpoint = isWA
        ? `/users/${profile.id}/send-whatsapp`
        : `/users/${profile.id}/send-email`;
      const body = isWA ? { message } : { subject, message };
      const res = await API.post(endpoint, body);
      toast.success(res.data?.message || "Mensaje enviado");
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error enviando mensaje");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[20000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <div className={`px-5 py-4 flex items-center justify-between ${isWA ? "bg-emerald-500" : "bg-blue-500"}`}>
          <div className="flex items-center gap-2 text-white font-bold">
            {isWA ? <MessageSquare size={18} /> : <Mail size={18} />}
            {isWA ? "Enviar WhatsApp" : "Enviar Email"}
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition">
            <X size={18} />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-sm text-gray-600">
            Para: <span className="font-semibold text-gray-800">{profile.name}</span>
            <span className="text-gray-400 ml-1">
              ({isWA ? (profile.phoneCountryCode ? `${profile.phoneCountryCode} ` : "") + profile.phone : profile.email})
            </span>
          </div>
          {!isWA && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Asunto</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder="Asunto del correo…"
              />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">Mensaje</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
              placeholder="Escribe tu mensaje aquí…"
            />
          </div>
          <button
            onClick={handleSend}
            disabled={sending}
            className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm text-white transition disabled:opacity-60 ${
              isWA ? "bg-emerald-500 hover:bg-emerald-600" : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {sending ? <RefreshCw size={15} className="animate-spin" /> : <Send size={15} />}
            {sending ? "Enviando…" : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Collapsible Section ───────────────────────────────────────────────────────
function Section({ title, icon, open, onToggle, children }) {
  return (
    <div className="border border-gray-100 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <span className="text-gray-400">{icon}</span>
        <span className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex-1">{title}</span>
        <ChevronDown size={14} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-3">{children}</div>}
    </div>
  );
}

// ── Profile Drawer ────────────────────────────────────────────────────────────
// Panel deslizante con los datos de un usuario (contacto, pedidos, reseñas,
// QPoints) y acciones para contactarlo (WhatsApp/email, verificación) — se usa
// tanto en el panel de usuarios del admin como en cualquier otra pantalla que
// necesite "ver el perfil y contactar" a un usuario (ej. ganador de un sorteo).
export default function UserProfileDrawer({ userId, onClose, onUpdated }) {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [expanded, setExpanded] = useState({ orders: true, points: false, reviews: false, addresses: true });
  const [compose, setCompose] = useState(null); // "whatsapp" | "email" | null

  const toggle = (key) => setExpanded((p) => ({ ...p, [key]: !p[key] }));

  const loadProfile = useCallback(() => {
    setLoading(true);
    API.get(`/users/${userId}/profile`)
      .then((res) => setProfile(res.data?.data?.user ?? null))
      .catch(() => toast.error("Error cargando perfil"))
      .finally(() => setLoading(false));
  }, [userId]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const act = async (key, fn, successMsg) => {
    setActionLoading(key);
    try {
      const res = await fn();
      toast.success(res?.data?.message || successMsg);
      loadProfile();
      onUpdated?.();
    } catch (e) {
      toast.error(e.response?.data?.message || "Error");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = () =>
    act("toggle", () => API.patch(`/users/${userId}/toggle-active`),
      profile?.isActive ? "Cuenta desactivada" : "Cuenta activada");

  const handleResendEmail = () =>
    act("email", () => API.post(`/users/${userId}/resend-verification-email`), "Email reenviado");

  const handleResendWhatsApp = () =>
    act("whatsapp", () => API.post(`/users/${userId}/resend-whatsapp`), "Código reenviado");

  // Portal a document.body — ver el mismo comentario en EventRaffleModal.jsx:
  // si un ancestro tiene una animación de Framer Motion (transform CSS),
  // position:fixed deja de cubrir toda la pantalla y se confina a ese ancestro.
  return createPortal(
    <>
    {compose && <ComposeModal type={compose} profile={profile} onClose={() => setCompose(null)} />}
    <div className="fixed inset-0 z-[9999] flex justify-end">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b z-10 px-5 py-4 flex items-center justify-between">
          <h2 className="font-bold text-gray-800 text-lg">Perfil de usuario</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <RefreshCw className="animate-spin text-gray-300" size={32} />
          </div>
        ) : !profile ? (
          <div className="flex-1 flex items-center justify-center text-gray-400">
            Perfil no encontrado
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-5 pb-28 space-y-5">
            {/* Avatar + Identity */}
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex-shrink-0 shadow">
                {profile.avatar ? (
                  <img
                    src={getImageUrl(profile.avatar)}
                    alt={profile.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-2xl font-bold text-gray-400">
                    {profile.name?.[0]?.toUpperCase()}
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-xl text-gray-900 leading-tight">{profile.name}</h3>
                <p className="text-gray-500 text-sm truncate">{profile.email}</p>
                {profile.phone && (
                  <p className="text-gray-400 text-xs flex items-center gap-1 mt-0.5">
                    <Phone size={11} />
                    {profile.phoneCountryCode} {profile.phone}
                  </p>
                )}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[profile.role] ?? "bg-gray-100 text-gray-600"}`}>
                    {profile.role}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${profile.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>
                    {profile.isActive ? "● Activo" : "● Inactivo"}
                  </span>
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="grid grid-cols-3 gap-2">
              <div className={`rounded-xl p-3 flex flex-col items-center gap-1 ${profile.emailVerified ? "bg-blue-50 border border-blue-200" : "bg-gray-50 border border-gray-200"}`}>
                <Mail size={16} className={profile.emailVerified ? "text-blue-500" : "text-gray-400"} />
                <span className={`text-xs font-semibold text-center leading-tight ${profile.emailVerified ? "text-blue-600" : "text-gray-400"}`}>
                  {profile.emailVerified ? "Email\nverificado" : "Email\nsin verificar"}
                </span>
              </div>
              <div className={`rounded-xl p-3 flex flex-col items-center gap-1 ${profile.whatsappVerified ? "bg-emerald-50 border border-emerald-200" : "bg-gray-50 border border-gray-200"}`}>
                <MessageSquare size={16} className={profile.whatsappVerified ? "text-emerald-500" : "text-gray-400"} />
                <span className={`text-xs font-semibold text-center leading-tight ${profile.whatsappVerified ? "text-emerald-600" : "text-gray-400"}`}>
                  WhatsApp{"\n"}{profile.whatsappVerified ? "verificado" : "sin verificar"}
                </span>
              </div>
              <div className={`rounded-xl p-3 flex flex-col items-center gap-1 ${profile.verificationStatus === "aprobado" ? "bg-purple-50 border border-purple-200" : "bg-gray-50 border border-gray-200"}`}>
                <Shield size={16} className={profile.verificationStatus === "aprobado" ? "text-purple-500" : "text-gray-400"} />
                <span className={`text-xs font-semibold text-center leading-tight ${profile.verificationStatus === "aprobado" ? "text-purple-600" : "text-gray-400"}`}>
                  Docs{"\n"}{profile.verificationStatus}
                </span>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-amber-600">{profile.qpoints ?? 0}</div>
                <div className="text-xs text-amber-500 mt-0.5">QPoints</div>
              </div>
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-blue-600">{profile.orders?.length ?? 0}</div>
                <div className="text-xs text-blue-500 mt-0.5">Pedidos</div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-3 text-center">
                <div className="text-xl font-bold text-purple-600">{profile.reviews?.length ?? 0}</div>
                <div className="text-xs text-purple-500 mt-0.5">Reseñas</div>
              </div>
            </div>

            {/* QR + Dates */}
            <div className="bg-gray-50 rounded-xl p-4 flex gap-4 items-center">
              <div className="bg-white p-2 rounded-xl shadow-sm flex-shrink-0">
                <QRCodeSVG
                  value={profile.referralCode || profile.id}
                  size={80}
                  level="M"
                />
              </div>
              <div className="min-w-0">
                {profile.referralCode && (
                  <p className="text-xs text-gray-500 mb-1">
                    Referido: <span className="font-mono font-bold text-gray-800">{profile.referralCode}</span>
                  </p>
                )}
                <p className="text-xs text-gray-500">
                  Registro:{" "}
                  <span className="text-gray-700 font-medium">
                    {dayjs(profile.createdAt).format("DD MMM YYYY")}
                  </span>
                </p>
                <p className="text-xs text-gray-400 mt-0.5">{dayjs(profile.createdAt).fromNow()}</p>
                {profile.documentNumber && (
                  <p className="text-xs text-gray-500 mt-1">
                    {profile.documentType}: <span className="font-mono text-gray-700">{profile.documentNumber}</span>
                  </p>
                )}
              </div>
            </div>

            {/* Addresses */}
            {profile.addresses?.length > 0 && (
              <Section
                title={`Direcciones (${profile.addresses.length})`}
                icon={<MapPin size={14} />}
                open={expanded.addresses}
                onToggle={() => toggle("addresses")}
              >
                <div className="space-y-2">
                  {profile.addresses.map((addr) => (
                    <div key={addr.id} className="bg-gray-50 rounded-xl p-3 flex items-start gap-2">
                      <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-700">{addr.label}</div>
                        <div className="text-xs text-gray-500 truncate">{addr.address}, {addr.city}</div>
                      </div>
                      {addr.isDefault && (
                        <span className="px-1.5 py-0.5 bg-blue-100 text-blue-600 text-xs rounded-full flex-shrink-0">
                          Principal
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Orders */}
            {profile.orders?.length > 0 && (
              <Section
                title={`Últimos pedidos (${profile.orders.length})`}
                icon={<ShoppingBag size={14} />}
                open={expanded.orders}
                onToggle={() => toggle("orders")}
              >
                <div className="space-y-2">
                  {profile.orders.map((order) => (
                    <div key={order.id} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center gap-2">
                        {order.company?.logo && (
                          <img
                            src={getImageUrl(order.company.logo)}
                            alt=""
                            className="w-7 h-7 rounded-lg object-cover flex-shrink-0"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-800 truncate">
                            #{order.orderNumber ?? order.id.slice(-6).toUpperCase()} — {order.company?.name}
                          </div>
                          <div className="text-xs text-gray-400">
                            {dayjs(order.createdAt).format("DD MMM YYYY")} ·{" "}
                            ${Number(order.total ?? 0).toLocaleString("es-CO")}
                          </div>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0 ${ORDER_STATUS_COLORS[order.status] ?? "bg-gray-100 text-gray-600"}`}>
                          {ORDER_STATUS_LABELS[order.status] ?? order.status}
                        </span>
                      </div>
                      {order.items?.length > 0 && (
                        <div className="mt-1.5 text-xs text-gray-400">
                          {order.items.map((item, i) => (
                            <span key={i}>
                              {i > 0 && ", "}
                              {item.quantity}× {item.title}
                            </span>
                          ))}
                          {order.items.length >= 3 && "…"}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Reviews */}
            {profile.reviews?.length > 0 && (
              <Section
                title={`Reseñas (${profile.reviews.length})`}
                icon={<Star size={14} />}
                open={expanded.reviews}
                onToggle={() => toggle("reviews")}
              >
                <div className="space-y-2">
                  {profile.reviews.map((review) => (
                    <div key={review.id} className="bg-gray-50 rounded-xl p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-gray-700">{review.product?.company?.name ?? review.product?.title}</span>
                        <div className="flex items-center gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              size={11}
                              fill={i < review.rating ? "#f59e0b" : "none"}
                              className={i < review.rating ? "text-amber-400" : "text-gray-300"}
                            />
                          ))}
                          <span className="text-xs text-gray-500 ml-1">{review.rating}/5</span>
                        </div>
                      </div>
                      {review.comment && (
                        <p className="text-xs text-gray-500 italic">"{review.comment}"</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {dayjs(review.createdAt).format("DD MMM YYYY")}
                      </p>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* QPoints History */}
            {profile.qpointsHistory?.length > 0 && (
              <Section
                title={`Historial QPoints (${profile.qpointsHistory.length})`}
                icon={<Coins size={14} />}
                open={expanded.points}
                onToggle={() => toggle("points")}
              >
                <div className="space-y-1.5">
                  {profile.qpointsHistory.map((h) => (
                    <div key={h.id} className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2">
                      <span className={`text-sm font-bold w-12 text-right flex-shrink-0 ${h.points > 0 ? "text-green-600" : "text-red-500"}`}>
                        {h.points > 0 ? "+" : ""}{h.points}
                      </span>
                      <span className="text-xs text-gray-600 flex-1 truncate">
                        {h.description || h.action}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {dayjs(h.createdAt).format("DD/MM/YY")}
                      </span>
                    </div>
                  ))}
                </div>
              </Section>
            )}

            {/* Actions */}
            <div className="border-t pt-4 space-y-2">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">Acciones de cuenta</p>
              <button
                onClick={handleToggleActive}
                disabled={actionLoading === "toggle"}
                className={`flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm transition border disabled:opacity-60 ${
                  profile.isActive
                    ? "bg-red-50 text-red-600 hover:bg-red-100 border-red-200"
                    : "bg-green-50 text-green-600 hover:bg-green-100 border-green-200"
                }`}
              >
                {actionLoading === "toggle" ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : profile.isActive ? (
                  <><UserX size={15} /> Desactivar cuenta</>
                ) : (
                  <><UserCheck size={15} /> Activar cuenta</>
                )}
              </button>
              <button
                onClick={handleResendEmail}
                disabled={!!profile.emailVerified || actionLoading === "email"}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 border border-blue-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {actionLoading === "email" ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  <><Mail size={15} /> {profile.emailVerified ? "Email ya verificado" : "Reenviar email verificación"}</>
                )}
              </button>
              <button
                onClick={handleResendWhatsApp}
                disabled={!profile.phone || actionLoading === "whatsapp"}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm bg-emerald-50 text-emerald-600 hover:bg-emerald-100 border border-emerald-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                {actionLoading === "whatsapp" ? (
                  <RefreshCw size={15} className="animate-spin" />
                ) : (
                  <><MessageSquare size={15} /> {profile.phone ? "Reenviar código WhatsApp" : "Sin teléfono registrado"}</>
                )}
              </button>

              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mt-4 mb-3">Mensajes personalizados</p>
              <button
                onClick={() => setCompose("whatsapp")}
                disabled={!profile.phone}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm bg-green-50 text-green-700 hover:bg-green-100 border border-green-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <MessageSquare size={15} />
                {profile.phone ? "Enviar WhatsApp personalizado" : "Sin teléfono para WhatsApp"}
              </button>
              <button
                onClick={() => setCompose("email")}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl font-semibold text-sm bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200 transition"
              >
                <Mail size={15} /> Enviar email personalizado
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
    </>,
    document.body
  );
}
